// @ts-check
// Estado próprio do construtor do público (contrato 09 de público, seção 9): signals das regras e grupos (as mesmas
// funções puras do filtro avançado das views), dos atalhos, da view da fonte, da busca e do modo de destinatário. O pai
// recebe a definição a cada mudança e grava quando quiser.
import { signal } from '@preact/signals';
import { audienceFromViewState, normalizeAudienceDefinition } from '../../../../shared/audiences/audience-definition.js';
import { applyFilterTreeAction, findRootGroup } from '../../lib/filter-tree-state.js';
import { generateId } from '../../lib/utils.js';

/**
 * @typedef {import('../../../../shared/audiences/audience-definition.js').AudienceDefinition} AudienceDefinition
 * @typedef {import('../../lib/view-filters.js').ViewFilter} ViewFilter
 * @typedef {import('../views/view-state.js').ViewFilterGroup} ViewFilterGroup
 * @typedef {ReturnType<typeof createAudienceStore>} AudienceStore
 */

/**
 * @param {unknown} initial definição guardada (normalizada aqui)
 * @param {{ usage?: 'AUDIENCE' | 'CONDITION', onChange?: (definition: AudienceDefinition) => void }} [options]
 */
export function createAudienceStore(initial, options = {}) {
  const { definition: start } = normalizeAudienceDefinition(initial ?? { version: 1 }, { usage: options.usage });
  const filters = signal(/** @type {ViewFilter[]} */ (start.filters));
  const filterGroups = signal(/** @type {ViewFilterGroup[]} */ (start.filterGroups));
  const presets = signal(start.presets);
  const viewId = signal(start.viewId);
  const recordIds = signal(start.recordIds);
  const anyFieldSearch = signal(start.anyFieldSearch);
  const recipientMode = signal(start.recipients.mode);
  // a cada mudança o contador sobe: a prévia espera e pede de novo
  const revision = signal(0);

  /** @returns {AudienceDefinition} */
  const toDefinition = () => ({
    version: 1,
    objectNameSingular: 'enrollmentOpportunity',
    viewId: viewId.value,
    recordIds: recordIds.value,
    anyFieldSearch: anyFieldSearch.value,
    presets: presets.value,
    filters: /** @type {any} */ (filters.value),
    filterGroups: /** @type {any} */ (filterGroups.value),
    recipients: { mode: recipientMode.value },
  });

  const changed = () => {
    revision.value += 1;
    options.onChange?.(toDefinition());
  };

  return {
    filters,
    filterGroups,
    presets,
    viewId,
    recordIds,
    anyFieldSearch,
    recipientMode,
    revision,
    toDefinition,
    /** Ação do editor E/OU. @param {{ type: string, [key: string]: any }} action */
    dispatch(action) {
      const next = applyFilterTreeAction({ filters: filters.value, filterGroups: filterGroups.value }, action, generateId);
      filters.value = next.filters;
      filterGroups.value = next.filterGroups;
      changed();
    },
    /** Primeira condição: cria o grupo raiz com uma regra. @param {string} fieldMetadataId @param {string} operand */
    addFirstRule(fieldMetadataId, operand) {
      this.dispatch({ type: 'ensureRoot', fieldMetadataId, operand });
    },
    /** "Todas as condições" ou "qualquer uma": o operador do grupo raiz. @param {'AND' | 'OR'} logicalOperator */
    setMatch(logicalOperator) {
      const root = findRootGroup({ filters: filters.value, filterGroups: filterGroups.value });
      if (root) this.dispatch({ type: 'setGroupOperator', groupId: root.id, logicalOperator });
    },
    /** @param {Partial<AudienceDefinition['presets']>} changes */
    setPresets(changes) {
      presets.value = { ...presets.value, ...changes };
      changed();
    },
    /** @param {string | null} nextViewId */
    setViewId(nextViewId) {
      viewId.value = nextViewId;
      changed();
    },
    /** @param {'POINT_OF_CONTACT' | 'ALL_GUARDIANS'} mode */
    setRecipientMode(mode) {
      recipientMode.value = mode;
      changed();
    },
    /** @param {string} text */
    setSearch(text) {
      anyFieldSearch.value = text.slice(0, 100);
      changed();
    },
    /**
     * "Copiar os filtros para editar": os filtros da view entram literais no público e a view sai da fonte. Recusa
     * (devolve os problemas) quando passa dos limites ou de um nível de subgrupo.
     * @param {{ viewFilters?: any[], viewFilterGroups?: any[], anyFieldFilterValue?: string | null }} view
     * @returns {Array<{ code: string }>}
     */
    copyViewFilters(view) {
      const copied = audienceFromViewState({ filters: view.viewFilters ?? [], filterGroups: view.viewFilterGroups ?? [], anyFieldSearch: view.anyFieldFilterValue ?? '' });
      if (copied.problems.length > 0) return copied.problems;
      filters.value = /** @type {any} */ (copied.definition.filters);
      filterGroups.value = /** @type {any} */ (copied.definition.filterGroups);
      anyFieldSearch.value = copied.definition.anyFieldSearch;
      viewId.value = null;
      changed();
      return [];
    },
  };
}
